import React, { createContext, useContext, useEffect, useState } from 'react';
import {
  signIn,
  signOut,
  signUp,
  confirmSignUp,
  fetchUserAttributes,
  getCurrentUser,
  confirmSignIn,
  fetchAuthSession,
  resetPassword,
  confirmResetPassword
} from 'aws-amplify/auth';
import { Amplify } from 'aws-amplify';
import { awsConfig } from '../services/auth/aws-exports';
import { useNavigate } from 'react-router-dom';
import toast from '../utils/notify';

const AwsCognitoContext = createContext();
const AwsCognitoActionsContext = createContext();

export const useAwsCognito = () => useContext(AwsCognitoContext);
export const useAwsCognitoActions = () => useContext(AwsCognitoActionsContext);

// Configure Amplify
Amplify.configure(awsConfig);

export const AWSAuthProvider = ({ children }) => {
  const [awsCognitoData, setAwsCognitoData] = useState({
    user: null,
    isAuthenticated: false,
    isLoading: true,
  });

  const navigate = useNavigate();

  // Check if user is already authenticated on mount
  useEffect(() => {
    const checkCurrentUser = async () => {
      setAwsCognitoData((prev) => ({ ...prev, isLoading: true }));
      try {
        const user = await getCurrentUser();
        if (user) {
          const userData = await fetchUserAttributes();

          setAwsCognitoData({
            user: userData,
            isAuthenticated: true,
            isLoading: false,
          });
        } else {
          setAwsCognitoData({
            user: null,
            isAuthenticated: false,
            isLoading: false
          });
        }
      } catch (error) {
        console.log('No authenticated user on mount:', error.name);
        setAwsCognitoData({
          user: null,
          isAuthenticated: false,
          isLoading: false
        });
      }
    };

    checkCurrentUser();
  }, []);

  // Refresh session periodically (every 50 minutes)
  useEffect(() => {
    const refreshSession = async () => {
      try {
        const session = await fetchAuthSession();
        if (session?.tokens?.idToken) {
          const isTokenValid = session.tokens.idToken.payload.exp * 1000 > Date.now();

          if (!isTokenValid) {
            // Token expired, logout
            await logout();
          }
        }
      } catch (error) {
        console.error('Error refreshing session:', error);
        await logout();
      }
    };

    const interval = setInterval(refreshSession, 50 * 60 * 1000); // 50 minutes
    return () => clearInterval(interval);
  }, []);

  // Sign In User
  const signInUser = async ({ email, password }) => {
    setAwsCognitoData((prev) => ({ ...prev, isLoading: true }));

    try {
      const result = await signIn({
        username: email,
        password: password,
      });

      console.log({ result });

      if (result.isSignedIn) {
        const userData = await fetchUserAttributes();
        const session = await fetchAuthSession();

        setAwsCognitoData({
          user: userData,
          isAuthenticated: true,
          isLoading: false,
        });
        navigate('/', { replace: true });
        return { success: true };
      }
      else if (result.nextStep) {
        // Handle MFA or other challenges
        const { signInStep } = result.nextStep;

        if (signInStep === 'CONFIRM_SIGN_IN_WITH_SMS_CODE') {
          toast('Please enter the SMS code sent to your phone');
          return { success: false, nextStep: 'SMS_MFA', challengeData: result };
        }
        else if (signInStep === 'CONFIRM_SIGN_IN_WITH_NEW_PASSWORD_REQUIRED') {
          toast('Please set a new password');
          return { success: false, nextStep: 'NEW_PASSWORD_REQUIRED', challengeData: result };
        }
        else if (signInStep === 'CONFIRM_SIGN_IN_WITH_TOTP_CODE') {
          toast('Please enter your TOTP code');
          return { success: false, nextStep: 'TOTP_MFA', challengeData: result };
        }
        else if (signInStep === 'RESET_PASSWORD') {
          // An administrator reset this password; Cognito has emailed a code.
          return { success: false, nextStep: 'RESET_PASSWORD' };
        }
      }
    } catch (error) {
      console.error('Error signing in:', error);
      setAwsCognitoData({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });

      return { success: false, error: error.message || 'Login failed' };
    }
  };

  // Confirm Sign In (for MFA and NEW_PASSWORD_REQUIRED)
  const confirmSignInChallenge = async (challengeResponse) => {
    try {
      console.log('🔐 confirmSignInChallenge called with:', challengeResponse);

      // Handle different challenge response formats
      let confirmSignInParams;

      if (typeof challengeResponse === 'object' && challengeResponse.newPassword) {
        // NEW_PASSWORD_REQUIRED challenge - pass newPassword as challengeResponse
        confirmSignInParams = {
          challengeResponse: challengeResponse.newPassword
        };
        console.log('📝 Using NEW_PASSWORD_REQUIRED flow');
      } else if (typeof challengeResponse === 'string') {
        // MFA or other string-based challenges
        confirmSignInParams = {
          challengeResponse: challengeResponse
        };
        console.log('🔢 Using MFA code flow');
      } else {
        throw new Error('Invalid challengeResponse format');
      }

      console.log('🔐 Calling confirmSignIn with params:', confirmSignInParams);
      const result = await confirmSignIn(confirmSignInParams);
      console.log('✅ confirmSignIn result:', result);

      if (result.isSignedIn) {
        const userData = await fetchUserAttributes();
        const session = await fetchAuthSession();

        setAwsCognitoData({
          user: userData,
          isAuthenticated: true,
          isLoading: false,
        });

        toast.success('Authentication successful!');
        navigate('/');
        return { success: true };
      }
    } catch (error) {
      console.error('Error confirming sign in:', error);
      toast.error(error.message || 'Authentication failed');
      return { success: false, error: error.message };
    }
  };

  // Sign Up User
  const signUpUser = async ({ email, password, name }) => {
    setAwsCognitoData((prev) => ({ ...prev, isLoading: true }));

    try {
      const result = await signUp({
        username: email,
        password,
        options: {
          userAttributes: {
            email,
            name,
          },
        },
      });

      setAwsCognitoData((prev) => ({ ...prev, isLoading: false }));
      toast.success('Signup successful! Please check your email for verification code.');

      return {
        success: true,
        email,
        nextStep: result.nextStep
      };
    } catch (error) {
      console.error('Error signing up:', error);
      setAwsCognitoData((prev) => ({ ...prev, isLoading: false }));
      toast.error(error.message || 'Signup failed');
      return { success: false, error: error.message };
    }
  };

  // Confirm Sign Up
  const confirmSignUpUser = async (email, code) => {
    setAwsCognitoData((prev) => ({ ...prev, isLoading: true }));

    try {
      await confirmSignUp({
        username: email,
        confirmationCode: code,
      });

      setAwsCognitoData((prev) => ({ ...prev, isLoading: false }));
      toast.success('Email verified successfully! You can now sign in.');
      navigate('/signin');
      return { success: true };
    } catch (error) {
      console.error('Error confirming sign up:', error);
      setAwsCognitoData((prev) => ({ ...prev, isLoading: false }));
      toast.error(error.message || 'Verification failed');
      return { success: false, error: error.message };
    }
  };

  // Forgot Password - Initiate
  const forgotPassword = async (email) => {
    try {
      const result = await resetPassword({ username: email });
      toast.success('Password reset code sent to your email');
      return { success: true, nextStep: result.nextStep };
    } catch (error) {
      console.error('Error initiating password reset:', error);
      toast.error(error.message || 'Failed to send reset code');
      return { success: false, error: error.message };
    }
  };

  // Forgot Password - Confirm
  const confirmForgotPassword = async (email, code, newPassword) => {
    try {
      await confirmResetPassword({
        username: email,
        confirmationCode: code,
        newPassword,
      });

      toast.success('Password reset successful! You can now sign in.');
      navigate('/login');
      return { success: true };
    } catch (error) {
      console.error('Error confirming password reset:', error);
      toast.error(error.message || 'Password reset failed');
      return { success: false, error: error.message };
    }
  };

  // Logout
  const logout = async (redirectTo = '/signin') => {
    setAwsCognitoData((prev) => ({ ...prev, isLoading: true }));

    try {
      await signOut();

      setAwsCognitoData({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });

      toast.success('Logged out successfully');
      navigate(redirectTo);
    } catch (error) {
      console.error('Error signing out:', error);
      setAwsCognitoData({
        user: null,
        isAuthenticated: false,
        isLoading: false,
      });
      toast.error('Logout failed');
    }
  };

  return (
    <AwsCognitoContext.Provider value={awsCognitoData}>
      <AwsCognitoActionsContext.Provider
        value={{
          signIn: signInUser,
          confirmSignIn: confirmSignInChallenge,
          signUp: signUpUser,
          confirmSignUp: confirmSignUpUser,
          forgotPassword,
          confirmForgotPassword,
          logout,
        }}
      >
        {children}
      </AwsCognitoActionsContext.Provider>
    </AwsCognitoContext.Provider>
  );
};
